import { db, functions } from "@/firebase";
import router from "@/router";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const getDefaultState = () => {
  return {
    classifications: [],
    document: {},
    documents: [],
    status: {},
    templates: [],
    waiting: false,
  };
};

const state = getDefaultState();

const actions = {
  async createDocument({ commit }, data) {
    try {
      commit("wait");
      const createDocument = httpsCallable(functions, "createDocument");
      const res = await createDocument(data);

      if (!res.data.success) throw "unknown";

      commit("addDocument", res.data.data);
      router.push(`/documents/${res.data.data.id}`);
    } catch (error) {
      commit("failure", error);
    }
  },
  async createTemplate({ commit }, data) {
    try {
      commit("wait");
      const createTemplate = httpsCallable(functions, "createTemplate");
      const res = await createTemplate(data);

      if (!res.data.success) throw "unknown";

      commit("addTemplate", res.data.data);
      router.push(`/documents/${res.data.data.id}`);
    } catch (error) {
      commit("failure", error);
    }
  },
  async getDocument({ commit }, id) {
    try {
      commit("wait");
      const docRef = doc(db, "documents", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const document = docSnap.data();
        document.id = docSnap.id;
        commit("setDocument", document);
      }
    } catch (error) {
      commit("failure", error);
    }
  },
  async getClassifications({ commit }) {
    try {
      commit("wait");
      const querySnapshot = await getDocs(collection(db, "classifications"));
      const classifications = querySnapshot.docs.map((doc) => {
        const classification = doc.data();
        classification.id = doc.id;
        return classification;
      });
      commit("setClassifications", classifications);
    } catch (error) {
      commit("failure", error);
    }
  },
  async getDocuments({ commit }, estateId) {
    try {
      commit("wait");
      const q = query(
        collection(db, "documents"),
        where("estateId", "==", estateId),
        where("isTemplate", "==", false)
      );
      const querySnapshot = await getDocs(q);
      const documents = querySnapshot.docs.map((doc) => {
        const document = doc.data();
        document.id = doc.id;
        return document;
      });

      commit("setDocuments", documents);
    } catch (error) {
      commit("failure", error);
    }
  },
  async getSignableDocument({ commit }, data) {
    try {
      commit("wait");
      const getSignableDocument = httpsCallable(
        functions,
        "getSignableDocument"
      );
      const res = await getSignableDocument(data);

      if (!res.data.success) throw "unknown";

      commit("setDocument", res.data.data);
    } catch (error) {
      commit("failure", error);
    }
  },
  async getTemplates({ commit }, estateId) {
    try {
      commit("wait");
      const q = query(
        collection(db, "documents"),
        where("estateId", "==", estateId),
        where("isTemplate", "==", true)
      );
      const querySnapshot = await getDocs(q);
      const templates = querySnapshot.docs.map((doc) => {
        const template = doc.data();
        template.id = doc.id;
        return template;
      });

      commit("setTemplates", templates);
    } catch (error) {
      commit("failure", error);
    }
  },
  async signDocument({ commit }, data) {
    try {
      commit("wait");
      const signDocument = httpsCallable(functions, "signDocument");
      const res = await signDocument(data);

      if (!res.data.success) throw "unknown";

      window.location.replace(res.data.data);
    } catch (error) {
      commit("failure", error);
    }
  },
  async updateDocument({ commit }, data) {
    try {
      commit("wait");
      const updateDocument = httpsCallable(functions, "updateDocument");
      const res = await updateDocument(data);

      if (!res.data.success) throw "unknown";

      commit("replaceDocument", res.data.data);
      router.push(`/documents/${res.data.data.id}`);
    } catch (error) {
      commit("failure", error);
    }
  },
  async updateTemplate({ commit }, data) {
    try {
      commit("wait");
      const updateTemplate = httpsCallable(functions, "updateDocument");
      const res = await updateTemplate(data);

      if (!res.data.success) throw "unknown";

      commit("replaceTemplate", res.data.data);
      router.push(`/documents/${res.data.data.id}`);
    } catch (error) {
      commit("failure", error);
    }
  },
  async verifySigning({ commit }, data) {
    try {
      commit("wait");
      const verifyDocumentSigning = httpsCallable(
        functions,
        "verifyDocumentSigning"
      );
      const res = await verifyDocumentSigning(data);

      if (!res.data.success) throw "unknown";

      commit("setSuccess", "document_signed");
      commit("resetState");
    } catch (error) {
      commit("failure", error);
    }
  },
};

const mutations = {
  addDocument(state, document) {
    state.document = document;
    state.documents.push(document);
    state.waiting = false;
  },
  addTemplate(state, template) {
    state.document = template;
    state.templates.push(template);
    state.waiting = false;
  },
  failure(state, error) {
    state.status.error = error;
    setTimeout(() => {
      state.status = {};
    }, 100);
    state.waiting = false;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  replaceDocument(state, document) {
    const index = state.documents.findIndex((d) => d.id == document.id);

    if (index != -1) {
      state.documents.splice(index, 1, document);
    }
    state.document = document;
    state.waiting = false;
  },
  replaceTemplate(state, template) {
    const index = state.templates.findIndex((t) => t.id == template.id);

    if (index != -1) {
      state.templates.splice(index, 1, template);
    }
    state.document = template;
    state.waiting = false;
  },
  setClassifications(state, classifications) {
    state.classifications = classifications;
    state.waiting = false;
  },
  setDocument(state, document) {
    state.document = document;
    state.waiting = false;
  },
  setDocuments(state, documents) {
    state.documents = documents;
    state.waiting = false;
  },
  setTemplates(state, templates) {
    state.templates = templates;
    state.waiting = false;
  },
  setSuccess(state, code) {
    state.status.success = { code };
    setTimeout(() => {
      state.status = {};
    }, 100);
  },
  wait(state) {
    state.waiting = true;
  },
};

const getters = {
  getDocument(state) {
    return state.document;
  },
};

const document = {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
};

export default document;
